import React, { useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import Col from "antd/es/col";
import Drawer from "antd/es/drawer";
import { Row } from "antd/es/grid";
import { useIntl } from "react-intl";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import { EntityType, EntityTypeSupportingDataMerging } from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import ColumnModel from "@mapmycustomers/shared/types/viewModel/internalModel/ColumnModel";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { LoadingSpinner } from "@mapmycustomers/ui";

import ButtonLink from "@app/component/ButtonLink";
import ColumnPane from "@app/component/SelectColumnsPane/ColumnPane";
import localSettings from "@app/config/LocalSettings";
import { isSelectedEntitiesLoading } from "@app/scene/settings/component/MergeDuplicates/store";
import { dataMergingAnalytics } from "@app/scene/settings/settingsAnaytics";
import { RootState } from "@app/store/rootReducer";
import ColumnVisibility from "@app/types/viewModel/ColumnVisibility";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import { getEntityTypeDisplayName } from "@app/util/ui";
import useHiddenChat from "@app/util/useHiddenChat";

import DupeReason from "./component/DupeReason";
import FieldsTable from "./component/FieldsTable";
import InfoField from "./component/InfoField";
import Navigation from "./component/Navigation";
import OwnerWarning from "./component/OwnerWarning";
import styles from "./PreviewMergePane.module.scss";

const DEFAULT_COLUMNS: Record<EntityTypeSupportingDataMerging, string[]> = {
  [EntityType.COMPANY]: ["name", "address", "phone", "website", "color"],
  [EntityType.PERSON]: ["name", "address", "email", "phone", "color"],
};

interface Props {
  entityType: EntityTypeSupportingDataMerging;
  loading: boolean;
  onHide: () => void;
}

const PreviewMergePane: React.FC<Props> = ({ entityType, loading, onHide }) => {
  const intl = useIntl();
  const [selectColumnVisible, showSelectColumn, hideSelectColumn] = useBoolean();
  const [showedColumns, setShowedColumns] = useState<Set<string>>(
    new Set(localSettings.getMergingDuplicatePaneColumns(entityType) ?? DEFAULT_COLUMNS[entityType])
  );

  const columns: ColumnModel<IField> = useMemo(
    () =>
      getFieldModelByEntityType(entityType).fields.map((field) => ({
        field,
        visible: showedColumns.has(field.name),
      })),
    [entityType, showedColumns]
  );

  const handleChangeColumnVisibility = useCallback(
    (visibilities: ColumnVisibility[]) => {
      const columns = visibilities
        .filter(({ visible }) => visible)
        .map(({ field: { name } }) => name);
      localSettings.setMergingDuplicatePaneColumns(entityType, columns);
      setShowedColumns(new Set(columns));
    },
    [entityType, setShowedColumns]
  );

  const handleFilterField = useCallback(
    (field: IField) => !field.hasFeature(FieldFeature.ADDRESS),
    []
  );

  useHiddenChat();

  return (
    <Drawer
      className={styles.container}
      closable
      onClose={onHide}
      placement="right"
      title={intl.formatMessage({
        id: "settings.mergeDuplicates.previewMergePane.title",
        defaultMessage: "Review & merge potential duplicates",
        description: "Settings Merge duplicates - title of preview/merge pane",
      })}
      visible
    >
      {loading ? (
        <LoadingSpinner global />
      ) : (
        <div className={styles.wrapper}>
          <Row justify="space-between">
            <Col>
              {intl.formatMessage({
                id: "settings.mergeDuplicates.previewMergePane.description",
                defaultMessage:
                  "Read the instructions below, then select a primary record to merge data.",
                description: "Settings Merge duplicates - description of preview/merge pane",
              })}
            </Col>
            <Col>
              <ButtonLink
                onClick={() => {
                  dataMergingAnalytics?.clicked([
                    getEntityTypeDisplayName(intl, entityType as EntityType, {
                      lowercase: false,
                      plural: true,
                    }),
                    "Review & Merge",
                    "Select fields to compare",
                  ]);
                  showSelectColumn();
                }}
              >
                {intl.formatMessage({
                  id: "settings.mergeDuplicates.previewMergePane.selectFields",
                  defaultMessage: "Select fields to compare",
                  description: "Settings Merge duplicates - preview/merge pane - select fields",
                })}
              </ButtonLink>
            </Col>
          </Row>
          <InfoField />
          <DupeReason />
          <OwnerWarning />
          <FieldsTable showedColumns={showedColumns} />
          <ColumnPane
            columns={columns}
            entityType={entityType}
            fieldFilter={handleFilterField}
            onHide={hideSelectColumn}
            onUpdate={handleChangeColumnVisibility}
            visible={selectColumnVisible}
          />
        </div>
      )}
      <Navigation entityType={entityType} />
    </Drawer>
  );
};

const mapStateToProps = (state: RootState) => ({
  loading: isSelectedEntitiesLoading(state),
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(PreviewMergePane);
